/**
 * Converts basic markdown to HTML
 * @param text - The markdown text to convert
 * @returns The converted HTML string
 */
export const formatMarkdown = (text: string = ''): string => {
    if (!text.trim()) return '';

    const lines = text.split('\n');
    const htmlParts: string[] = [];

    const stack: { indent: number; tag: string }[] = [];

    const closeUntil = (indent: number) => {
        while (stack.length > 0 && stack[stack.length - 1].indent >= indent) {
            const { tag } = stack.pop()!;
            htmlParts.push(`</${tag}>`);
        }
    };

    for (let rawLine of lines) {
        const line = rawLine.replace(/\r$/, '');
        const indentMatch = line.match(/^(\s*)/);
        const indent = indentMatch ? indentMatch[1].length : 0;
        const trimmed = line.trim();

        if (!trimmed) {
            continue;
        }

        // Header parsing
        if (/^#{1,6} /.test(trimmed)) {
            closeUntil(0);
            const headerLevel = trimmed.match(/^#+/)![0].length;
            const content = trimmed.replace(/^#+\s*/, '');
            htmlParts.push(`<h${headerLevel}>${content}</h${headerLevel}>`);
            continue;
        }

        // Horizontal rule
        if (/^(-{3,}|_{3,}|\*{3,})$/.test(trimmed)) {
            closeUntil(0);
            htmlParts.push('<hr>');
            continue;
        }

        // List item
        const listMatch = trimmed.match(/^-\s+(.+)/);
        if (listMatch) {
            const content = listMatch[1]
                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.+?)\*/g, '<em>$1</em>')
                .replace(/ {2,}$/g, '<br>'); // preserve trailing spaces

            // Open a new <ul> if stack is empty or indent increased
            if (stack.length === 0 || indent > stack[stack.length - 1].indent) {
                htmlParts.push('<ul>');
                stack.push({ indent, tag: 'ul' });
            } else if (indent < stack[stack.length - 1].indent) {
                closeUntil(indent);
                if (stack.length === 0 || stack[stack.length - 1].tag !== 'ul') {
                    htmlParts.push('<ul>');
                    stack.push({ indent, tag: 'ul' });
                }
            }

            htmlParts.push(`<li>${content}</li>`);
            continue;
        }

        // Paragraph fallback
        closeUntil(0);
        const paragraph = trimmed
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>');
        htmlParts.push(`<p>${paragraph}</p>`);
    }

    closeUntil(0); // close any remaining open tags

    return htmlParts.join('\n').trim();
};

